import { Injectable } from '@angular/core';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { CurrencyCacheService } from '../services';

dayjs.extend(utc);

@Injectable()
export class Utl {
  private static _emailRegex: any =
    /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

  static isValidEmail(email: string) {
    return email && email.match(this._emailRegex);
  }

  static isoDate(date: Date): string {
    return dayjs(date).utc().format('YYYY-MM-DD');
  }

  // Local-calendar YYYY-MM-DD (no UTC shift) — for date-picker values like settlementDate.
  static isoDateLocal(date: Date): string {
    return dayjs(date).format('YYYY-MM-DD');
  }

  // Local date-time with the browser's offset, e.g. 2026-09-10T14:05:00+04:00 —
  // the instant is exact and the date part is still the operator's own day.
  static isoDateTimeLocal(date: Date): string {
    return dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ');
  }

  /**
   * How long something took, e.g. `4m 37s`. Returns '' when either end is
   * missing or the range is inverted — which is the normal state for a run
   * still in flight, so callers can render it unconditionally.
   *
   * Accepts both the ISO strings and the epoch millis this API mixes.
   */
  static durationLabel(
    start: string | number | null | undefined,
    end: string | number | null | undefined
  ): string {
    if (start === null || start === undefined) return '';
    if (end === null || end === undefined) return '';

    const from = new Date(start).getTime();
    const to = new Date(end).getTime();
    if (Number.isNaN(from) || Number.isNaN(to) || to < from) return '';

    const seconds = Math.round((to - from) / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    if (hours) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    if (minutes) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
    return `${rest}s`;
  }

  static startOfDayWithTimezone(date: Date): number {
    return dayjs(date).startOf('day').toDate().getTime();
  }

  static endOfDayWithTimezone(date: Date): number {
    return dayjs(date).endOf('day').toDate().getTime();
  }

  static convertTimeStringToTimestamp(timeStr: string): number {
    let timeComponents = timeStr.split(':');
    let result = 0;
    if (timeComponents.length) {
      result = parseInt(timeComponents[0]) * 3600;
      if (timeComponents.length > 1) {
        result += parseInt(timeComponents[1]) * 60;
      }
    }
    return result * 1000;
  }

  static exportHandler(result: any, fileName: string) {
    const bom = '\uFEFF';
    const blob = new Blob([bom, result], { type: 'text/csv;charset=utf-8' });
    const nav = window.navigator as any;
    if (nav.msSaveOrOpenBlob) {
      nav.msSaveBlob(blob, fileName);
    } else {
      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      // IE: "Access is denied";
      // see: https://connect.microsoft.com/IE/feedback/details/797361/ie-10-treats-blob-url-as-cross-origin-and-denies-access
      a.click();
      document.body.removeChild(a);
    }
  }

  static exportErrorMessage(
    err: any,
    fallback = 'Error exporting CSV'
  ): string {
    if (
      err?.type === 'NO_DATA_FOR_EXPORT' ||
      err?.message === 'NO_DATA_FOR_EXPORT'
    ) {
      return 'No data for Export';
    }
    return fallback;
  }

  static initDateRange(months = 1): { startDate: Date; endDate: Date } {
    const endDate = new Date();
    const startDate = new Date(
      new Date(endDate).setMonth(endDate.getMonth() - months)
    );
    return { startDate, endDate };
  }

  static dateRangeToTimestamps(
    start: Date | null,
    end: Date | null
  ): { startTs: number; endTs: number } {
    return {
      startTs: start ? Utl.startOfDayWithTimezone(start) : 0,
      endTs: end ? Utl.endOfDayWithTimezone(end) : 0,
    };
  }

  static formatFixed(value: string | number, precision: number) {
    if (typeof value === 'string') {
      value = parseFloat(value);
    }

    return value.toFixed ? value.toFixed(precision) : null;
  }

  static formatPrice(symbol: string, price: string | number) {
    if (typeof price === 'string') {
      price = parseFloat(price);
    }

    const symbols = CurrencyCacheService.getSymbolsFromCache();

    if (!symbols || !symbols[symbol]) {
      return price.toFixed(8);
    }

    return price.toFixed(symbols[symbol].pricePrecision);
  }

  static formatTotal(symbol: string, price: string | number) {
    if (typeof price === 'string') {
      price = parseFloat(price);
    }

    const symbols = CurrencyCacheService.getSymbolsFromCache();
    if (!symbols || !symbols[symbol]) {
      return price.toFixed(6);
    }

    return price.toFixed(symbols[symbol].totalPrecision);
  }

  static formatQuantity(symbol: string, price: string | number) {
    if (typeof price === 'string') {
      price = parseFloat(price);
    }

    const symbols = CurrencyCacheService.getSymbolsFromCache();
    if (!symbols || !symbols[symbol]) {
      return price.toFixed(6);
    }

    return price.toFixed(symbols[symbol].amountPrecision);
  }

  static applyQueryParams<T extends object>(
    params: any,
    target: T,
    defaults: Partial<T>
  ): T {
    const result: any = {};

    for (const key of Object.keys(defaults)) {
      // @ts-ignore
      result[key] = params[key] ?? defaults[key];
    }

    return Object.assign(target, result);
  }

  static formatAmount(
    currency: string,
    quantity: string | number | null = null
  ) {
    const currencyObj = CurrencyCacheService.get(currency);
    if (!quantity) {
      return 0;
    }
    if (!currencyObj) {
      return null;
    }
    if (typeof quantity === 'string') {
      quantity = parseFloat(quantity);
    }

    return quantity.toFixed(currencyObj.amountPrecision);
  }

  static getParams(url: string) {
    const params: any = {};
    const vars = url.split('&');

    for (let i = 0; i < vars.length; i++) {
      const pair = vars[i].split('=');
      params[pair[0]] = decodeURIComponent(pair[1]);
    }

    return params;
  }

  static removeItemsFromArray(arr: any[], removed: any[]): any[] {
    let what;
    let length = removed.length;
    let deletedItem;

    while (length && arr.length) {
      what = removed[--length];
      while ((deletedItem = arr.indexOf(what)) !== -1) {
        arr.splice(deletedItem, 1);
      }
    }
    return arr;
  }
}
